import { detectImportFormat, normalizeImportColumn, prepareImportColumns, validateImportMapping, type ImportColumn, type ImportField } from "@easy-core/spreadsheet";
import { normalizeDefaultDesign } from "../../../packages/core/src/newsletterDesign";
import { readSpreadsheetRows, spreadsheetCellToString, writeSpreadsheetBlob, type SpreadsheetRow } from "@easy-core/spreadsheet";
import { resolveMountedBasename } from "@easy-core/router";
import type { ApiError } from "@easy-core/http";
import { newsletterTextToHtml } from "../../../packages/core/src/newsletterText";
import {
  NEWSLETTER_COMPOSE_STORAGE_KEY,
  type NewsletterComposeAssistantDraft,
  type NewsletterComposeDraft,
  type NewsletterDefaultDesignSnapshot,
} from "@easy-types";
import { formatRecurrenceSummary, normalizeEventRecurrence, type EventRecurrence } from "./eventPlannerRecurrence";
import { normalizeInternalName } from "./eventPlannerSlug";
export { normalizeInternalName, resolveAutoInternalName } from "./eventPlannerSlug";


export type EventConnectorStatus = {
  connector_key: string;
  label: string;
  status: "pending" | "outdated" | "synced" | "failed" | "disabled" | string;
  status_revision: number | null;
  is_current: boolean | null;
  external_id: string;
  external_url: string;
  last_error: string;
  last_synced_at: string | null;
  created_at: string | null;
  updated_at: string | null;
};

export type ConnectorSettingsRecord = {
  connector_key: string;
  label: string;
  is_configured: boolean;
  endpoint_url: string;
  external_reference: string;
  username: string;
  secret: string;
  notes: string;
  created_at: string | null;
  updated_at: string | null;
};

export type CalDAVSettingsRecord = {
  connector_key: "caldav";
  configured: boolean;
  calendar_url: string;
  username: string;
  timezone: string;
  password: string;
  has_password: boolean;
  created_at: string | null;
  updated_at: string | null;
};

export type IcsFeedSettingsRecord = {
  connector_key: "ics_feed";
  configured: boolean;
  calendar_title: string;
  feed_url: string;
  has_token: boolean;
  created_at: string | null;
  updated_at: string | null;
};

export type EventPublicPageDisplayRecord = {
  theme_preset: string;
  accent_color: string;
  secondary_color: string;
  brand_name: string;
  logo_url: string;
  intro_title: string;
  intro_body: string;
  show_filters: boolean;
  show_calendar: boolean;
  show_featured_event: boolean;
  show_intro: boolean;
  show_site_link: boolean;
  show_past_events_toggle: boolean;
};

export type EventPublicPageSettingsRecord = {
  enabled: boolean;
  website_url: string;
  website_label: string;
  public_url: string;
  preview_url: string;
  embed_url: string;
  display: EventPublicPageDisplayRecord;
  updated_at: string | null;
};

export type EventRecord = {
  id: number;
  internal_name: string;
  display_name: string;
  date: string;
  start_time: string;
  end_time: string;
  publication_status: "draft" | "published" | string;
  timezone: string;
  location_name: string;
  location_address: string;
  location_street_number: string;
  location_street: string;
  location_postal_code: string;
  location_city: string;
  recurrence: EventRecurrence;
  location?: string;
  description: string;
  practical_info: string;
  facebook_url: string;
  revision: number;
  price: {
    label: string;
    amount_cents: number | null;
    currency: string;
  };
  illustration: {
    kind: "none" | "external_url" | "asset_ref" | string;
    reference: string;
    alt_text: string;
  };
  connectors: EventConnectorStatus[];
  created_at: string | null;
  updated_at: string | null;
};

export type EventMeta = {
  app_name: string;
  app_version: string;
  release_channel: string;
  app_id: string;
  default_timezone?: string;
  connectors: Array<{ key: string; label: string }>;
  connector_settings: Record<string, ConnectorSettingsRecord>;
};

export type SavedLocationSuggestion = {
  location_name: string;
  location_address: string;
  location_street_number: string;
  location_street: string;
  location_postal_code: string;
  location_city: string;
  usage_count: number;
  last_used_at: string | null;
};

export type EventFormState = {
  id: number | null;
  internal_name: string;
  display_name: string;
  date: string;
  start_time: string;
  end_time: string;
  publication_status: "draft" | "published";
  timezone: string;
  location_name: string;
  location_address: string;
  location_street_number: string;
  location_street: string;
  location_postal_code: string;
  location_city: string;
  recurrence: EventRecurrence;
  description: string;
  practical_info: string;
  facebook_url: string;
  price_label: string;
  price_amount: string;
  price_currency: string;
  illustration_kind: "none" | "external_url" | "asset_ref";
  illustration_reference: string;
  illustration_alt_text: string;
};

export type StatusTone = "neutral" | "success" | "error";

export type StatusState = {
  message: string;
  tone: StatusTone;
};

export type BulkEditFieldState = {
  enabled: boolean;
  value: string;
};

export type BulkEditFormState = {
  date: BulkEditFieldState;
  start_time: BulkEditFieldState;
  end_time: BulkEditFieldState;
  timezone: BulkEditFieldState;
  location_name: BulkEditFieldState;
  location_street_number: BulkEditFieldState;
  location_street: BulkEditFieldState;
  location_postal_code: BulkEditFieldState;
  location_city: BulkEditFieldState;
  description: BulkEditFieldState;
  practical_info: BulkEditFieldState;
  facebook_url: BulkEditFieldState;
  price_label: BulkEditFieldState;
  price_amount: BulkEditFieldState;
  price_currency: BulkEditFieldState;
};

export type ImportPreviewEntry = {
  rowNumber: number;
  mode: "create" | "update";
  matchedEventId: number | null;
  form: EventFormState;
  issues: string[];
};

export type ImportPreview = {
  columns?: ImportColumn[];
  fileName: string;
  sheetName: string;
  entries: ImportPreviewEntry[];
  readyCount: number;
  issueCount: number;
  createCount: number;
  updateCount: number;
};

export type EventFilters = {
  search: string;
  publication_status: "all" | "draft" | "published";
  schedule: "all" | "upcoming" | "past";
  facebook: "all" | "with_link" | "without_link";
  connector: "all" | "attention" | "pending" | "outdated" | "synced" | "failed" | "disabled";
};

const EVENT_DATE_FORMATTER = new Intl.DateTimeFormat("fr-FR", {
  day: "2-digit",
  month: "short",
  year: "numeric",
});

const EVENT_DATE_WITH_WEEKDAY_SHORT_FORMATTER = new Intl.DateTimeFormat("fr-FR", {
  weekday: "short",
  day: "2-digit",
  month: "short",
});

const NEWSLETTER_PERIOD_DATE_FORMATTER = new Intl.DateTimeFormat("fr-FR", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

const NEWSLETTER_EVENT_DATE_FORMATTER = new Intl.DateTimeFormat("fr-FR", {
  weekday: "long",
  day: "numeric",
  month: "long",
});

const IMPORT_COLUMN_ALIASES: Record<string, string[]> = {
  id: ["id", "event_id", "identifiant"],
  internal_name: ["internal_name", "internal", "slug", "nom_interne", "code", "reference"],
  display_name: ["display_name", "name", "nom", "title", "titre", "event_name", "evenement"],
  date: ["date", "event_date", "jour", "date_evenement"],
  start_time: ["start_time", "heure_debut", "debut", "start", "start_hour", "time_start"],
  end_time: ["end_time", "heure_fin", "fin", "end", "end_hour", "time_end"],
  publication_status: ["publication_status", "event_status", "status_diffusion", "statut_diffusion", "status"],
  timezone: ["timezone", "fuseau", "fuseau_horaire", "time_zone", "timezone_name", "tz"],
  location_name: ["location_name", "location", "lieu", "place", "venue", "nom_lieu", "location_title"],
  location_street_number: ["location_street_number", "street_number", "numero", "numero_voie", "num"],
  location_street: ["location_street", "street", "rue", "adresse_ligne", "address_line"],
  location_postal_code: ["location_postal_code", "postal_code", "code_postal", "zip", "zipcode"],
  location_city: ["location_city", "city", "ville", "commune"],
  location_address: [
    "location_address",
    "location_adress",
    "address",
    "adresse",
    "localisation",
    "adresse_lieu",
  ],
  description: ["description", "desc", "details"],
  practical_info: ["practical_info", "infos_pratiques", "practical", "info", "practical_information"],
  facebook_url: ["facebook_url", "facebook", "facebook_link", "lien_facebook", "url_facebook"],
  price_label: ["price_label", "tarif", "prix", "price_text", "label_prix"],
  price_amount: ["price_amount", "price", "amount", "montant", "montant_prix"],
  price_currency: ["price_currency", "currency", "devise"],
  illustration_kind: ["illustration_kind", "image_kind", "visuel_type"],
  illustration_reference: [
    "illustration_reference",
    "illustration_url",
    "image",
    "image_url",
    "visuel",
    "visuel_url",
  ],
  illustration_alt_text: ["illustration_alt_text", "image_alt", "alt", "image_alt_text"],
};

export const importFields: ImportField[] = [
  { key: "id", label: "Identifiant" },
  { key: "internal_name", label: "Nom interne" },
  { key: "display_name", label: "Nom affiché", required: true, requiredUnless: ["id", "internal_name"] },
  { key: "date", label: "Date" },
  { key: "start_time", label: "Heure de début" },
  { key: "end_time", label: "Heure de fin" },
  { key: "publication_status", label: "Statut de publication" },
  { key: "timezone", label: "Fuseau horaire" },
  { key: "location_name", label: "Nom du lieu" },
  { key: "location_street_number", label: "Numéro de rue" },
  { key: "location_street", label: "Rue" },
  { key: "location_postal_code", label: "Code postal" },
  { key: "location_city", label: "Ville" },
  { key: "location_address", label: "Adresse complète" },
  { key: "description", label: "Description" },
  { key: "practical_info", label: "Informations pratiques" },
  { key: "facebook_url", label: "Lien Facebook" },
  { key: "price_label", label: "Libellé du tarif" },
  { key: "price_amount", label: "Montant" },
  { key: "price_currency", label: "Devise" },
  { key: "illustration_kind", label: "Type d’illustration" },
  { key: "illustration_reference", label: "Référence de l’illustration" },
  { key: "illustration_alt_text", label: "Texte alternatif" },
];

const IMPORT_TEMPLATE_HEADERS = [
  "id",
  "internal_name",
  "display_name",
  "date",
  "start_time",
  "end_time",
  "publication_status",
  "timezone",
  "location_name",
  "location_street_number",
  "location_street",
  "location_postal_code",
  "location_city",
  "location_address",
  "description",
  "practical_info",
  "facebook_url",
  "price_label",
  "price_amount",
  "price_currency",
  "illustration_kind",
  "illustration_reference",
  "illustration_alt_text",
] as const;

const DEFAULT_IMPORTED_EVENT_PUBLICATION_STATUS = "draft" as const;

const IMPORT_TEMPLATE_SAMPLE_ROW = [
  "",
  "salon-livre-automne",
  "Salon du livre d'automne",
  "2026-10-05",
  "10:00",
  "18:00",
  "draft",
  "Europe/Paris",
  "Halle des expositions",
  "",
  "Route de Saint-Joseph",
  "44300",
  "Nantes",
  "Route de Saint-Joseph, Nantes",
  "Rencontres, dedicaces et programmation sur toute la journee.",
  "Ouverture des portes a 09:30. Entree libre.",
  "https://facebook.com/events/exemple",
  "Entree libre",
  "0.00",
  "EUR",
  "external_url",
  "https://cdn.example.com/affiche-salon.jpg",
  "Affiche du salon du livre",
] as const;

export const eventFieldIds = [
  "internal_name",
  "display_name",
  "date",
  "start_time",
  "end_time",
  "publication_status",
  "timezone",
  "location_name",
  "location_street_number",
  "location_street",
  "location_postal_code",
  "location_city",
  "location_address",
  "description",
  "practical_info",
  "facebook_url",
  "price",
  "illustration",
] as const;

export function apiUrl(path: string) {
  const normalizedPath = `api/${String(path || "").replace(/^\/+/, "")}`;
  const basename = resolveMountedBasename("event-planner");
  if (basename === "/") {
    return `/${normalizedPath}`;
  }
  return `${basename}/${normalizedPath}`.replace(/\/{2,}/g, "/");
}

export function detectUserTimezone(defaultTimezone = "Europe/Paris") {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return String(timezone || "").trim() || defaultTimezone;
  } catch {
    return defaultTimezone;
  }
}

export function composeEventAddress(parts: {
  location_street_number?: string;
  location_street?: string;
  location_postal_code?: string;
  location_city?: string;
  location_address?: string;
}) {
  const streetLine = [parts.location_street_number, parts.location_street]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(" ");
  const localityLine = [parts.location_postal_code, parts.location_city]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(" ");
  return [streetLine, localityLine].filter(Boolean).join(", ") || String(parts.location_address || "").trim();
}

export function parseEventAddress(value: string) {
  const rawValue = String(value || "").trim();
  if (!rawValue) {
    return {
      location_street_number: "",
      location_street: "",
      location_postal_code: "",
      location_city: "",
    };
  }

  const segments = rawValue
    .split(",")
    .map((segment) => segment.trim())
    .filter(Boolean);
  const streetSegment = segments[0] || rawValue;
  const localitySegment = segments.length > 1 ? segments[segments.length - 1] : "";

  const streetMatch = streetSegment.match(/^(?<number>\d+[A-Za-z-]*)\s+(?<street>.+)$/u);
  let location_street_number = streetMatch?.groups?.number?.trim() || "";
  let location_street = streetMatch?.groups?.street?.trim() || streetSegment;
  let location_postal_code = "";
  let location_city = "";

  if (localitySegment) {
    const localityMatch = localitySegment.match(/^(?:(?<postal>\d{4,6})\s+)?(?<city>.+)$/u);
    location_postal_code = localityMatch?.groups?.postal?.trim() || "";
    location_city = localityMatch?.groups?.city?.trim() || "";
  } else if (!streetMatch) {
    const fullMatch = rawValue.match(/^(?<street>.+?)\s*,?\s*(?:(?<postal>\d{4,6})\s+)?(?<city>[A-Za-zÀ-ÿ'’\-\s]+)$/u);
    if (fullMatch?.groups?.street && fullMatch.groups.city) {
      location_street = fullMatch.groups.street.trim();
      location_postal_code = fullMatch.groups.postal?.trim() || "";
      location_city = fullMatch.groups.city.trim();
    }
  }

  return {
    location_street_number,
    location_street,
    location_postal_code,
    location_city,
  };
}

export function formatEventLocation(
  event: Pick<
    EventRecord,
    "location_name" | "location_address" | "location_street_number" | "location_street" | "location_postal_code" | "location_city"
  > & { location?: string }
) {
  const locationName = String(event.location_name || "").trim();
  const locationAddress = composeEventAddress(event);
  if (locationName && locationAddress) {
    return `${locationName}, ${locationAddress}`;
  }
  if (locationName || locationAddress) {
    return locationName || locationAddress;
  }
  return String(event.location || "").trim();
}

export function createEmptyEventForm(defaultTimezone = detectUserTimezone()): EventFormState {
  return {
    id: null,
    internal_name: "",
    display_name: "",
    date: "",
    start_time: "",
    end_time: "",
    publication_status: "draft",
    timezone: defaultTimezone,
    location_name: "",
    location_address: "",
    location_street_number: "",
    location_street: "",
    location_postal_code: "",
    location_city: "",
    recurrence: { segments: [], overrides: [] },
    description: "",
    practical_info: "",
    facebook_url: "",
    price_label: "",
    price_amount: "",
    price_currency: "EUR",
    illustration_kind: "none",
    illustration_reference: "",
    illustration_alt_text: "",
  };
}

export function compareEventSchedule(left: Pick<EventRecord, "date" | "start_time" | "id">, right: Pick<EventRecord, "date" | "start_time" | "id">) {
  const leftHasDate = Boolean(left.date);
  const rightHasDate = Boolean(right.date);
  if (leftHasDate !== rightHasDate) {
    return leftHasDate ? -1 : 1;
  }

  const leftHasStartTime = Boolean(left.start_time);
  const rightHasStartTime = Boolean(right.start_time);
  if (leftHasStartTime !== rightHasStartTime) {
    return leftHasStartTime ? -1 : 1;
  }

  const scheduleCompare = `${left.date} ${left.start_time}`.localeCompare(`${right.date} ${right.start_time}`);
  if (scheduleCompare !== 0) {
    return scheduleCompare;
  }

  return left.id - right.id;
}

function parseIsoDate(date: string) {
  if (!date) {
    return null;
  }

  const [year, month, day] = date.split("-").map((value) => Number.parseInt(value, 10));
  if (!year || !month || !day) {
    return null;
  }

  const marker = new Date(Date.UTC(year, month - 1, day));
  return Number.isNaN(marker.getTime()) ? null : marker;
}

function toIsoDate(date: Date) {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function addDaysToIso(date: string, days: number) {
  const marker = parseIsoDate(date);
  if (!marker) {
    return date;
  }
  marker.setUTCDate(marker.getUTCDate() + days);
  return toIsoDate(marker);
}

function escapeHtml(value: string) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function renderRichTextLine(value: string) {
  return escapeHtml(value).replace(/\r?\n/g, "<br />");
}

function hasEventPriceValue(event: Pick<EventRecord, "price">) {
  return typeof event.price.amount_cents === "number" || Boolean(String(event.price.label || "").trim());
}

function normalizeNewsletterRange(startDate: string, endDate: string) {
  if (!startDate && !endDate) {
    return { startDate: "", endDate: "" };
  }
  if (!startDate) {
    return { startDate: endDate, endDate };
  }
  if (!endDate) {
    return { startDate, endDate: startDate };
  }
  return startDate <= endDate ? { startDate, endDate } : { startDate: endDate, endDate: startDate };
}

function sortEventsForNewsletter(events: EventRecord[]) {
  return [...events].sort(compareEventSchedule);
}

function formatNewsletterDate(date: string) {
  const marker = parseIsoDate(date);
  if (!marker) {
    return date || "-";
  }
  return NEWSLETTER_PERIOD_DATE_FORMATTER.format(marker);
}

function formatNewsletterEventLine(event: Pick<EventRecord, "date" | "start_time" | "end_time">) {
  const marker = parseIsoDate(event.date);
  const dateLabel = marker ? NEWSLETTER_EVENT_DATE_FORMATTER.format(marker) : event.date || "-";
  if (event.start_time && event.end_time) {
    return `${dateLabel} - ${event.start_time} à ${event.end_time}`;
  }
  if (event.start_time) {
    return `${dateLabel} - ${event.start_time}`;
  }
  return dateLabel;
}

function buildNewsletterHtml(events: EventRecord[], startDate: string, endDate: string) {
  const periodLabel = formatNewsletterPeriodLabel(startDate, endDate);
  const introLabel =
    events.length > 0
      ? `${events.length} ${pluralize(events.length, "évènement")} publié${events.length > 1 ? "s" : ""} sur cette période.`
      : "Aucun évènement publié n'est programmé sur cette période pour le moment.";

  const eventSections = events.length
    ? events
        .map((event) => {
          const locationLabel = formatEventLocation(event);
          const descriptionHtml = event.description
            ? `<p style="margin:0 0 12px;">${renderRichTextLine(event.description)}</p>`
            : "";
          const practicalInfoHtml = event.practical_info
            ? `<p style="margin:0 0 12px;"><strong>Infos :</strong> ${renderRichTextLine(event.practical_info)}</p>`
            : "";
          const locationHtml = locationLabel
            ? `<p style="margin:0 0 12px;"><strong>Lieu :</strong> ${escapeHtml(locationLabel)}</p>`
            : "";
          const priceHtml = hasEventPriceValue(event)
            ? `<p style="margin:0;"><strong>Tarif :</strong> ${escapeHtml(formatEventPrice(event))}</p>`
            : "";
          const facebookHtml = event.facebook_url
            ? `<p style="margin:14px 0 0;"><a href="${escapeHtml(event.facebook_url)}" style="display:inline-block;padding:10px 14px;border-radius:999px;background:#4b7552;color:#ffffff;font-weight:700;text-decoration:none;">Voir sur Facebook</a></p>`
            : "";

          return [
            '<section style="margin:0 0 26px;padding:0 0 26px;border-bottom:1px solid #d7decc;">',
            `<h3 style="margin:0 0 12px;font-size:24px;line-height:1.22;color:#232c3a;">${escapeHtml(event.display_name)}</h3>`,
            `<p style="margin:0 0 12px;"><strong>${escapeHtml(formatNewsletterEventLine(event))}</strong></p>`,
            locationHtml,
            descriptionHtml,
            practicalInfoHtml,
            priceHtml,
            facebookHtml,
            "</section>",
          ].join("");
        })
        .join("")
    : '<section style="padding:22px;border-radius:18px;background:#f4f0df;border:1px solid #d7decc;color:#5f675f;">Aucun évènement publié n\'a été trouvé dans cette période. Élargissez les dates pour générer une newsletter.</section>';

  return [
    '<div style="font-family:Georgia, \'Times New Roman\', serif;color:#232c3a;line-height:1.65;">',
    '<p style="margin:0 0 14px;"><strong>Bonjour à vous,</strong></p>',
    '<p style="margin:0 0 20px;"><strong>Voici les évènements à venir !</strong></p>',
    `<h2 style="margin:0 0 10px;font-size:32px;line-height:1.2;color:#232c3a;">Les évènements ${escapeHtml(periodLabel)}</h2>`,
    `<p style="margin:0 0 26px;color:#5f675f;">${escapeHtml(introLabel)}</p>`,
    eventSections,
    "</div>",
  ].join("");
}

function buildNewsletterAssistantSectionBody(event: EventRecord) {
  const lines = [formatNewsletterEventLine(event)];
  const locationLabel = formatEventLocation(event);
  if (locationLabel) {
    lines.push(`Lieu : ${locationLabel}`);
  }
  if (event.description.trim()) {
    lines.push(event.description.trim());
  }
  if (event.practical_info.trim()) {
    lines.push(`Infos : ${event.practical_info.trim()}`);
  }
  if (hasEventPriceValue(event)) {
    lines.push(`Tarif : ${formatEventPrice(event)}`);
  }

  return lines.filter(Boolean).join("\n\n");
}

function buildNewsletterAssistantDraft(
  events: EventRecord[],
  startDate: string,
  endDate: string,
  subject: string,
  defaultDesign?: NewsletterDefaultDesignSnapshot | null
): NewsletterComposeAssistantDraft {
  const fallbackTheme = {
    style: "editorial" as const,
    primary_color: "",
    accent_color: "",
  };
  const safeDefaultDesign = normalizeNewsletterDefaultDesign(defaultDesign);
  const designSections = safeDefaultDesign?.sections || [];

  return {
    template_id: safeDefaultDesign?.template_id || "events",
    theme: safeDefaultDesign?.theme || fallbackTheme,
    text_align: safeDefaultDesign?.text_align,
    header_logo_url: safeDefaultDesign?.header_logo_url || "",
    header_logo_alt: safeDefaultDesign?.header_logo_alt || "",
    intro_kicker: "Évènements",
    intro_title: subject,
    intro_body:
      events.length > 0
        ? `${events.length} ${pluralize(events.length, "évènement")} publié${events.length > 1 ? "s" : ""} sélectionné${events.length > 1 ? "s" : ""} sur la période ${formatNewsletterPeriodLabel(startDate, endDate)}.`
        : `Aucun évènement publié n'a été trouvé sur la période ${formatNewsletterPeriodLabel(startDate, endDate)}.`,
    intro_image_url: safeDefaultDesign?.intro_image_url || "",
    intro_image_alt: safeDefaultDesign?.intro_image_alt || "",
    intro_image_layout: safeDefaultDesign?.intro_image_layout,
    intro_image_width_ratio: safeDefaultDesign ? safeDefaultDesign.intro_image_width_ratio ?? null : undefined,
    intro_image_height: safeDefaultDesign?.intro_image_height,
    sections: events.map((event, index) => {
      const designSection = designSections[index % Math.max(1, designSections.length)];
      return {
        id: `easy-event-${event.id}`,
        title: event.display_name,
        body: buildNewsletterAssistantSectionBody(event),
        body_html: newsletterTextToHtml(buildNewsletterAssistantSectionBody(event))
          .replace(/^<p>([^]*?)<\/p>/, (_match, line) => `<p><strong>${line}</strong></p>`)
          .replace(/<p>(Lieu|Infos|Tarif) : /g, "<p><strong>$1 :</strong> "),
        image_url: event.illustration.kind === "external_url" ? event.illustration.reference : "",
        image_alt: event.illustration.alt_text || event.display_name,
        button_label: event.facebook_url ? "Voir l'évènement" : "",
        button_url: event.facebook_url || "",
        text_align: designSection?.text_align,
        media_layout: designSection?.media_layout,
        media_width_ratio: designSection?.media_width_ratio ?? null,
        media_height: designSection?.media_height,
      };
    }),
    closing_note: safeDefaultDesign?.closing_note || "",
    closing_note_html: safeDefaultDesign?.closing_note_html,
  };
}

function normalizeNewsletterDefaultDesign(
  value: NewsletterDefaultDesignSnapshot | null | undefined
): NewsletterDefaultDesignSnapshot | null {
  return normalizeDefaultDesign(value);
}

export function createEmptyBulkEditForm(): BulkEditFormState {
  return {
    date: { enabled: false, value: "" },
    start_time: { enabled: false, value: "" },
    end_time: { enabled: false, value: "" },
    timezone: { enabled: false, value: detectUserTimezone() },
    location_name: { enabled: false, value: "" },
    location_street_number: { enabled: false, value: "" },
    location_street: { enabled: false, value: "" },
    location_postal_code: { enabled: false, value: "" },
    location_city: { enabled: false, value: "" },
    description: { enabled: false, value: "" },
    practical_info: { enabled: false, value: "" },
    facebook_url: { enabled: false, value: "" },
    price_label: { enabled: false, value: "" },
    price_amount: { enabled: false, value: "" },
    price_currency: { enabled: false, value: "EUR" },
  };
}

export function formatNewsletterPeriodLabel(startDate: string, endDate: string) {
  const normalizedRange = normalizeNewsletterRange(startDate, endDate);
  if (!normalizedRange.startDate && !normalizedRange.endDate) {
    return "à définir";
  }
  if (!normalizedRange.endDate || normalizedRange.startDate === normalizedRange.endDate) {
    return `du ${formatNewsletterDate(normalizedRange.startDate)}`;
  }
  return `du ${formatNewsletterDate(normalizedRange.startDate)} au ${formatNewsletterDate(normalizedRange.endDate)}`;
}

export function suggestNewsletterPeriod(events: EventRecord[], fallbackStartDate: string) {
  const publishedEvents = sortEventsForNewsletter(
    events.filter((event) => normalizeEventPublicationStatus(event.publication_status, "published") === "published")
  );
  const upcomingEvents = publishedEvents.filter((event) => event.date >= fallbackStartDate);
  const referenceEvents = upcomingEvents.length > 0 ? upcomingEvents : publishedEvents;
  const startDate = referenceEvents[0]?.date || fallbackStartDate;
  return {
    startDate,
    endDate: addDaysToIso(startDate, 30),
  };
}

export function filterNewsletterEvents(events: EventRecord[], startDate: string, endDate: string) {
  const normalizedRange = normalizeNewsletterRange(startDate, endDate);
  if (!normalizedRange.startDate || !normalizedRange.endDate) {
    return [];
  }

  return sortEventsForNewsletter(
    events.filter(
      (event) =>
        normalizeEventPublicationStatus(event.publication_status, "published") === "published" &&
        event.date >= normalizedRange.startDate &&
        event.date <= normalizedRange.endDate
    )
  );
}

export function buildNewsletterComposeDraft(
  events: EventRecord[],
  startDate: string,
  endDate: string,
  defaultDesign?: NewsletterDefaultDesignSnapshot | null
): NewsletterComposeDraft {
  const normalizedRange = normalizeNewsletterRange(startDate, endDate);
  const newsletterEvents = filterNewsletterEvents(events, normalizedRange.startDate, normalizedRange.endDate);
  const periodLabel = formatNewsletterPeriodLabel(normalizedRange.startDate, normalizedRange.endDate);
  const subject = `Les évènements ${periodLabel}`;

  return {
    source: "event-planner",
    name: `Newsletter ${periodLabel}`,
    subject,
    html_body: buildNewsletterHtml(newsletterEvents, normalizedRange.startDate, normalizedRange.endDate),
    assistant_draft: buildNewsletterAssistantDraft(
      newsletterEvents,
      normalizedRange.startDate,
      normalizedRange.endDate,
      subject,
      defaultDesign
    ),
    created_at: new Date().toISOString(),
    source_event_ids: newsletterEvents.map((event) => event.id),
    period_start: normalizedRange.startDate,
    period_end: normalizedRange.endDate,
  };
}

export function persistNewsletterComposeDraft(draft: NewsletterComposeDraft) {
  try {
    window.localStorage.setItem(NEWSLETTER_COMPOSE_STORAGE_KEY, JSON.stringify(draft));
  } catch {
    throw new Error("Impossible de préparer la newsletter.");
  }
}

export async function copyHtmlToClipboard(html: string) {
  const container = document.createElement("div");
  container.innerHTML = html || "";
  const plainText = (container.innerText || container.textContent || "").trim();

  if (navigator.clipboard && typeof navigator.clipboard.write === "function" && typeof ClipboardItem !== "undefined") {
    await navigator.clipboard.write([
      new ClipboardItem({
        "text/html": new Blob([html], { type: "text/html" }),
        "text/plain": new Blob([plainText], { type: "text/plain" }),
      }),
    ]);
    return;
  }

  if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
    await navigator.clipboard.writeText(plainText);
    return;
  }

  throw new Error("Le presse-papiers n'est pas disponible dans ce navigateur.");
}

export async function copyTextToClipboard(text: string) {
  const normalizedText = text.trim();

  if (!normalizedText) {
    throw new Error("Aucun texte a copier.");
  }

  if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
    await navigator.clipboard.writeText(normalizedText);
    return;
  }

  throw new Error("Le presse-papiers n'est pas disponible dans ce navigateur.");
}

export function createDefaultEventFilters(): EventFilters {
  return {
    search: "",
    publication_status: "all",
    schedule: "upcoming",
    facebook: "all",
    connector: "all",
  };
}

export function normalizeEventFilters(filters?: Partial<EventFilters>): EventFilters {
  const defaults = createDefaultEventFilters();
  const schedule = filters?.schedule === "all" || filters?.schedule === "past" ? "all" : "upcoming";

  return {
    ...defaults,
    ...filters,
    schedule,
  };
}

export function createDefaultConnectorSettings(
  connectorKey: string,
  label: string,
  partial?: Partial<ConnectorSettingsRecord>
): ConnectorSettingsRecord {
  return {
    connector_key: connectorKey,
    label,
    is_configured: false,
    endpoint_url: "",
    external_reference: "",
    username: "",
    secret: "",
    notes: "",
    created_at: null,
    updated_at: null,
    ...partial,
  };
}

export function connectorSettingsFromMeta(meta?: EventMeta | null) {
  const settingsByKey = meta?.connector_settings || {};
  return (meta?.connectors || []).map((connector) =>
    createDefaultConnectorSettings(connector.key, connector.label, settingsByKey[connector.key])
  );
}

export function createDefaultCalDAVSettings(partial?: Partial<CalDAVSettingsRecord>): CalDAVSettingsRecord {
  return {
    connector_key: "caldav",
    configured: false,
    calendar_url: "",
    username: "",
    timezone: "Europe/Paris",
    password: "",
    has_password: false,
    created_at: null,
    updated_at: null,
    ...partial,
  };
}

export function createDefaultIcsFeedSettings(partial?: Partial<IcsFeedSettingsRecord>): IcsFeedSettingsRecord {
  return {
    connector_key: "ics_feed",
    configured: false,
    calendar_title: "Relyo · Évènements",
    feed_url: "",
    has_token: false,
    created_at: null,
    updated_at: null,
    ...partial,
  };
}

export function createDefaultEventPublicPageSettings(
  partial?: Partial<EventPublicPageSettingsRecord>
): EventPublicPageSettingsRecord {
  const { display: _partialDisplay, ...partialRecord } = partial || {};
  const display = {
    theme_preset: "linen",
    accent_color: "#D95F43",
    secondary_color: "#6F7D8C",
    brand_name: "",
    logo_url: "",
    intro_title: "",
    intro_body: "",
    show_filters: true,
    show_calendar: true,
    show_featured_event: true,
    show_intro: true,
    show_site_link: true,
    show_past_events_toggle: true,
    ...(partial?.display || {}),
  };
  return {
    enabled: false,
    website_url: "",
    website_label: "Retour au site",
    public_url: "",
    preview_url: "",
    embed_url: "",
    display,
    updated_at: null,
    ...partialRecord,
  };
}

export async function downloadImportTemplate(format: "csv" | "xlsx") {
  const templateRows = [Array.from(IMPORT_TEMPLATE_HEADERS), Array.from(IMPORT_TEMPLATE_SAMPLE_ROW)];

  if (format === "xlsx") {
    const blob = await writeSpreadsheetBlob(templateRows, "Évènements");
    triggerFileDownload(
      blob,
      "relyo-evenements-import-template.xlsx"
    );
    return;
  }

  const csvContent = templateRows
    .map((row) => row.map(escapeCsvCell).join(","))
    .join("\n");
  triggerFileDownload(new Blob([csvContent], { type: "text/csv;charset=utf-8" }), "relyo-evenements-import-template.csv");
}

export async function downloadEventExport(events: EventRecord[], format: "csv" | "xlsx") {
  const exportRows = events.map((event) => [
    String(event.id ?? ""),
    event.internal_name,
    event.display_name,
    event.date,
    event.start_time,
    event.end_time,
    event.publication_status,
    event.timezone,
    event.location_name,
    event.location_street_number,
    event.location_street,
    event.location_postal_code,
    event.location_city,
    event.location_address,
    event.description,
    event.practical_info,
    event.facebook_url,
    event.price.label,
    typeof event.price.amount_cents === "number" ? formatAmountCents(event.price.amount_cents) : "",
    event.price.currency || "EUR",
    event.illustration.kind,
    event.illustration.reference,
    event.illustration.alt_text,
  ]);
  const exportTable = [Array.from(IMPORT_TEMPLATE_HEADERS), ...exportRows];
  const exportSuffix = new Date().toISOString().slice(0, 10);

  if (format === "xlsx") {
    const blob = await writeSpreadsheetBlob(exportTable, "Évènements");
    triggerFileDownload(
      blob,
      `relyo-evenements-export-${exportSuffix}.xlsx`
    );
    return;
  }

  const csvContent = exportTable
    .map((row) => row.map((cell) => escapeCsvCell(String(cell ?? ""))).join(","))
    .join("\n");
  triggerFileDownload(
    new Blob([csvContent], { type: "text/csv;charset=utf-8" }),
    `relyo-evenements-export-${exportSuffix}.csv`
  );
}

export function configuredConnectorKeys(meta?: EventMeta | null) {
  return new Set(
    connectorSettingsFromMeta(meta)
      .filter((connector) => connector.is_configured)
      .map((connector) => connector.connector_key)
  );
}

export function visibleEventConnectors(
  event: Pick<EventRecord, "connectors">,
  configuredKeys?: ReadonlySet<string>
) {
  if (!configuredKeys || configuredKeys.size === 0) {
    return [];
  }
  return event.connectors.filter((connector) => configuredKeys.has(connector.connector_key));
}

export function toEventForm(event?: Partial<EventRecord> | null): EventFormState {
  if (!event) {
    return createEmptyEventForm();
  }

  return {
    id: typeof event.id === "number" ? event.id : null,
    internal_name: String(event.internal_name || ""),
    display_name: String(event.display_name || ""),
    date: String(event.date || ""),
    start_time: String(event.start_time || ""),
    end_time: String(event.end_time || ""),
    publication_status: normalizeEventPublicationStatus(String(event.publication_status || ""), "published"),
    timezone: String(event.timezone || detectUserTimezone()),
    location_name: String(event.location_name || ""),
    ...(() => {
      const fallbackAddress = parseEventAddress(String(event.location_address || ""));
      return {
        location_street_number: String(event.location_street_number || fallbackAddress.location_street_number || ""),
        location_street: String(event.location_street || fallbackAddress.location_street || ""),
        location_postal_code: String(event.location_postal_code || fallbackAddress.location_postal_code || ""),
        location_city: String(event.location_city || fallbackAddress.location_city || ""),
        recurrence: normalizeEventRecurrence(event.recurrence),
        location_address: composeEventAddress({
          location_street_number: String(event.location_street_number || fallbackAddress.location_street_number || ""),
          location_street: String(event.location_street || fallbackAddress.location_street || ""),
          location_postal_code: String(event.location_postal_code || fallbackAddress.location_postal_code || ""),
          location_city: String(event.location_city || fallbackAddress.location_city || ""),
          location_address: String(event.location_address || ""),
        }),
      };
    })(),
    description: String(event.description || ""),
    practical_info: String(event.practical_info || ""),
    facebook_url: String(event.facebook_url || ""),
    price_label: String(event.price?.label || ""),
    price_amount:
      typeof event.price?.amount_cents === "number" ? formatAmountCents(event.price.amount_cents) : "",
    price_currency: String(event.price?.currency || "EUR"),
    illustration_kind:
      event.illustration?.kind === "external_url" || event.illustration?.kind === "asset_ref"
        ? event.illustration.kind
        : "none",
    illustration_reference: String(event.illustration?.reference || ""),
    illustration_alt_text: String(event.illustration?.alt_text || ""),
  };
}

export function buildEventPayload(form: EventFormState) {
  const normalizedInternalName = normalizeInternalName(form.internal_name);
  const normalizedIllustrationKind =
    form.illustration_kind === "external_url" || form.illustration_kind === "asset_ref"
      ? form.illustration_kind
      : "none";

  return {
    event_id: form.id || undefined,
    internal_name: normalizedInternalName,
    display_name: form.display_name.trim(),
    date: normalizeImportedDate(form.date),
    start_time: normalizeImportedTime(form.start_time),
    end_time: normalizeImportedTime(form.end_time),
    publication_status: normalizeEventPublicationStatus(form.publication_status, "published"),
    timezone: form.timezone.trim() || detectUserTimezone(),
    location_name: form.location_name.trim(),
    location_street_number: form.location_street_number.trim(),
    location_street: form.location_street.trim(),
    location_postal_code: form.location_postal_code.trim(),
    location_city: form.location_city.trim(),
    recurrence: normalizeEventRecurrence(form.recurrence),
    location_address: composeEventAddress(form),
    description: form.description.trim(),
    practical_info: form.practical_info.trim(),
    facebook_url: form.facebook_url.trim(),
    price: {
      label: form.price_label.trim(),
      amount: normalizeImportedAmount(form.price_amount),
      currency: (form.price_currency || "EUR").trim().toUpperCase(),
    },
    illustration: {
      kind: normalizedIllustrationKind,
      reference: form.illustration_reference.trim(),
      alt_text: form.illustration_alt_text.trim(),
    },
  };
}

export function validateEventForm(form: EventFormState) {
  const displayName = form.display_name.trim();
  const eventDate = normalizeImportedDate(form.date);
  const startTime = normalizeImportedTime(form.start_time);
  const endTime = normalizeImportedTime(form.end_time);
  const publicationStatus = normalizeEventPublicationStatus(form.publication_status, "published");
  const timezone = form.timezone.trim();

  if (!displayName) {
    return { fieldErrors: ["display_name"], message: "Le nom d'affichage est obligatoire." };
  }
  if (publicationStatus === "published" && !eventDate) {
    return { fieldErrors: ["date"], message: "La date de l'évènement est obligatoire." };
  }
  if (publicationStatus === "published" && !startTime) {
    return { fieldErrors: ["start_time"], message: "L'horaire de début est obligatoire." };
  }
  if (!timezone) {
    return { fieldErrors: ["timezone"], message: "Le fuseau horaire est obligatoire." };
  }
  if (normalizeEventRecurrence(form.recurrence).segments.length > 0 && !eventDate) {
    return { fieldErrors: ["date", "recurrence"], message: "La date de l'évènement est obligatoire pour définir une récurrence." };
  }

  const startMarker = startTime ? new Date(`2000-01-01T${startTime}:00`) : null;
  const endMarker = endTime ? new Date(`2000-01-01T${endTime}:00`) : null;
  if (
    startMarker &&
    endMarker &&
    !Number.isNaN(startMarker.getTime()) &&
    !Number.isNaN(endMarker.getTime()) &&
    startMarker >= endMarker
  ) {
    return {
      fieldErrors: ["start_time", "end_time"],
      message: "L'horaire de fin doit être après l'horaire de début.",
    };
  }

  return null;
}

export function applyBulkEdit(event: EventRecord, bulkEditForm: BulkEditFormState): EventFormState {
  const nextForm = toEventForm(event);

  if (bulkEditForm.date.enabled) {
    nextForm.date = normalizeImportedDate(bulkEditForm.date.value);
  }
  if (bulkEditForm.start_time.enabled) {
    nextForm.start_time = normalizeImportedTime(bulkEditForm.start_time.value);
  }
  if (bulkEditForm.end_time.enabled) {
    nextForm.end_time = normalizeImportedTime(bulkEditForm.end_time.value);
  }
  if (bulkEditForm.timezone.enabled) {
    nextForm.timezone = bulkEditForm.timezone.value.trim();
  }
  if (bulkEditForm.location_name.enabled) {
    nextForm.location_name = bulkEditForm.location_name.value.trim();
  }
  const hasStructuredLocationUpdate =
    bulkEditForm.location_street_number.enabled ||
    bulkEditForm.location_street.enabled ||
    bulkEditForm.location_postal_code.enabled ||
    bulkEditForm.location_city.enabled;
  if (bulkEditForm.location_street_number.enabled) {
    nextForm.location_street_number = bulkEditForm.location_street_number.value.trim();
  }
  if (bulkEditForm.location_street.enabled) {
    nextForm.location_street = bulkEditForm.location_street.value.trim();
  }
  if (bulkEditForm.location_postal_code.enabled) {
    nextForm.location_postal_code = bulkEditForm.location_postal_code.value.trim();
  }
  if (bulkEditForm.location_city.enabled) {
    nextForm.location_city = bulkEditForm.location_city.value.trim();
  }
  if (hasStructuredLocationUpdate) {
    nextForm.location_address = composeEventAddress(nextForm);
  }
  if (bulkEditForm.description.enabled) {
    nextForm.description = bulkEditForm.description.value.trim();
  }
  if (bulkEditForm.practical_info.enabled) {
    nextForm.practical_info = bulkEditForm.practical_info.value.trim();
  }
  if (bulkEditForm.facebook_url.enabled) {
    nextForm.facebook_url = bulkEditForm.facebook_url.value.trim();
  }
  if (bulkEditForm.price_label.enabled) {
    nextForm.price_label = bulkEditForm.price_label.value.trim();
  }
  if (bulkEditForm.price_amount.enabled) {
    nextForm.price_amount = normalizeImportedAmount(bulkEditForm.price_amount.value);
  }
  if (bulkEditForm.price_currency.enabled) {
    nextForm.price_currency = (bulkEditForm.price_currency.value || "EUR").trim().toUpperCase();
  }

  return nextForm;
}

export function hasEnabledBulkEdit(bulkEditForm: BulkEditFormState) {
  return Object.values(bulkEditForm).some((field) => field.enabled);
}

function buildGeneratedImportInternalName(value: Pick<EventFormState, "display_name" | "date" | "start_time">) {
  const displayName = String(value.display_name || "").trim();
  if (!displayName) {
    return "";
  }
  return normalizeInternalName([displayName, value.date || "", value.start_time || ""].filter(Boolean).join(" "));
}

export function formatEventDate(date: string) {
  if (!date) {
    return "-";
  }

  const marker = new Date(`${date}T00:00:00`);
  if (Number.isNaN(marker.getTime())) {
    return date;
  }
  return EVENT_DATE_FORMATTER.format(marker);
}

export function formatEventDateWithWeekdayShort(date: string) {
  if (!date) {
    return "-";
  }

  const marker = new Date(`${date}T00:00:00`);
  if (Number.isNaN(marker.getTime())) {
    return date;
  }
  return EVENT_DATE_WITH_WEEKDAY_SHORT_FORMATTER.format(marker);
}

export function formatEventSchedule(event: Pick<EventRecord, "date" | "start_time" | "end_time">) {
  return `${formatEventDate(event.date)} · ${event.start_time || "--:--"} - ${event.end_time || "--:--"}`;
}

export function formatEventPrice(event: Pick<EventRecord, "price">) {
  if (typeof event.price.amount_cents === "number") {
    const amount = (event.price.amount_cents / 100).toFixed(2).replace(".", ",");
    return `${amount} ${event.price.currency || "EUR"}`;
  }
  if (event.price.label) {
    return event.price.label;
  }
  return "Non renseigne";
}

export function connectorStatusLabel(status: string) {
  switch (status) {
    case "synced":
      return "Synchro";
    case "failed":
      return "Erreur";
    case "outdated":
      return "A revoir";
    case "disabled":
      return "Desactive";
    case "pending":
    default:
      return "En attente";
  }
}

export function connectorStatusClassName(status: string) {
  return `pill connector-pill connector-pill-${status || "pending"}`;
}

export function publicationStatusLabel(status: string) {
  return normalizeEventPublicationStatus(status, "published") === "draft" ? "Brouillon" : "Publie";
}

export function publicationStatusClassName(status: string) {
  return `pill event-publication-pill event-publication-pill-${normalizeEventPublicationStatus(status, "published")}`;
}

export function pluralize(count: number, singular: string, plural?: string) {
  return count > 1 ? plural || `${singular}s` : singular;
}

export function statusClassName(status: StatusState) {
  if (status.tone === "error") {
    return "status-line error";
  }
  if (status.tone === "success") {
    return "status-line success";
  }
  return "status-line";
}

export function hasFieldError(fieldErrors: string[], fieldId: string) {
  return fieldErrors.includes(fieldId);
}

export function normalizeApiError(error: unknown) {
  return error as ApiError;
}

export function resolveFieldErrors(error: ApiError, fallback: string[] = []) {
  return Array.isArray(error.fieldErrors) && error.fieldErrors.length > 0 ? error.fieldErrors : fallback;
}

export function inferEventFieldErrors(message: string) {
  const lowered = String(message || "").toLowerCase();
  if (lowered.includes("nom interne")) {
    return ["internal_name"];
  }
  if (lowered.includes("nom d'affichage")) {
    return ["display_name"];
  }
  if (lowered.includes("date de l'evenement")) {
    return ["date"];
  }
  if (lowered.includes("horaire de debut")) {
    return ["start_time"];
  }
  if (lowered.includes("horaire de fin")) {
    return ["end_time"];
  }
  if (lowered.includes("statut de diffusion")) {
    return ["publication_status"];
  }
  if (lowered.includes("fuseau horaire")) {
    return ["timezone"];
  }
  if (lowered.includes("adresse du lieu")) {
    return ["location_address"];
  }
  if (lowered.includes("nom du lieu")) {
    return ["location_name"];
  }
  if (lowered.includes("lieu")) {
    return ["location_name", "location_address"];
  }
  if (lowered.includes("prix")) {
    return ["price"];
  }
  if (lowered.includes("recurrence")) {
    return ["recurrence"];
  }
  if (lowered.includes("illustration")) {
    return ["illustration"];
  }
  if (lowered.includes("facebook")) {
    return ["facebook_url"];
  }
  return [];
}

export function eventMatchesSearch(
  event: EventRecord,
  searchTerm: string,
  visibleConnectors: EventConnectorStatus[] = event.connectors
) {
  const query = String(searchTerm || "").trim().toLowerCase();
  if (!query) {
    return true;
  }

  const connectorText = visibleConnectors
    .map((connector) => `${connector.label} ${connector.status} ${connector.external_id} ${connector.external_url}`)
    .join(" ");

  return [
    event.display_name,
    event.internal_name,
    publicationStatusLabel(event.publication_status),
    event.date,
    event.start_time,
    event.end_time,
    event.timezone,
    event.location_name,
    event.location_street_number,
    event.location_street,
    event.location_postal_code,
    event.location_city,
    event.location_address,
    formatRecurrenceSummary(event.recurrence),
    formatEventLocation(event),
    event.description,
    event.practical_info,
    event.facebook_url,
    event.price.label,
    connectorText,
  ]
    .join(" ")
    .toLowerCase()
    .includes(query);
}

export function eventMatchesFilters(
  event: EventRecord,
  filters: EventFilters,
  referenceDate: string,
  visibleConnectors: EventConnectorStatus[] = event.connectors
) {
  if (!eventMatchesSearch(event, filters.search, visibleConnectors)) {
    return false;
  }

  if (filters.publication_status !== "all" && normalizeEventPublicationStatus(event.publication_status, "published") !== filters.publication_status) {
    return false;
  }

  if (filters.schedule !== "all" && !event.date) {
    return false;
  }

  if (filters.schedule === "upcoming" && event.date < referenceDate) {
    return false;
  }
  if (filters.schedule === "past" && event.date >= referenceDate) {
    return false;
  }

  if (filters.facebook === "with_link" && !event.facebook_url.trim()) {
    return false;
  }
  if (filters.facebook === "without_link" && Boolean(event.facebook_url.trim())) {
    return false;
  }

  if (filters.connector === "attention") {
    return visibleConnectors.some((connector) => connector.status === "failed" || connector.status === "outdated");
  }

  if (filters.connector !== "all") {
    return visibleConnectors.some((connector) => connector.status === filters.connector);
  }

  return true;
}

export async function parseImportFile(file: File, events: EventRecord[], mapping?: string[]): Promise<ImportPreview> {
  const { sheetName, rows } = await readSpreadsheetRows(file);
  if (!sheetName || rows.length === 0) {
    throw new Error("Le fichier ne contient aucune feuille exploitable.");
  }

  if (rows.length < 2) {
    throw new Error("Le fichier doit contenir une ligne d'entete et au moins une ligne de donnees.");
  }

  const [headerRow, ...dataRows] = rows;
  const columns = prepareImportColumns(rows, resolveImportColumnKey, importFields);
  const headerMap = mapping || columns.map(column => column.target);
  if (mapping) {
    const errors = validateImportMapping(mapping, importFields);
    if (mapping.length !== headerRow.length || errors.length) throw new Error(errors.join(" ") || "Correspondances invalides.");
  }
  const byId = new Map(events.map((event) => [event.id, event]));
  const byInternalName = new Map(events.map((event) => [normalizeInternalName(event.internal_name), event]));
  const entries = dataRows
    .map((row, index) => mapImportRow(row, headerMap, byId, byInternalName, index + 2))
    .filter((entry) => Boolean(entry.form.display_name || entry.form.internal_name || entry.form.date));

  const previewByInternalName = new Map<string, number[]>();
  entries.forEach((entry) => {
    const key = normalizeInternalName(entry.form.internal_name);
    if (!key) {
      return;
    }
    previewByInternalName.set(key, [...(previewByInternalName.get(key) || []), entry.rowNumber]);
  });

  entries.forEach((entry) => {
    const key = normalizeInternalName(entry.form.internal_name);
    const duplicates = previewByInternalName.get(key) || [];
    if (duplicates.length > 1) {
      entry.issues.push(`Nom interne en double dans le fichier (lignes ${duplicates.join(", ")}).`);
    }
  });

  const readyCount = entries.filter((entry) => entry.issues.length === 0).length;
  const updateCount = entries.filter((entry) => entry.mode === "update" && entry.issues.length === 0).length;
  const createCount = entries.filter((entry) => entry.mode === "create" && entry.issues.length === 0).length;

  return {
    columns: columns.map((column, index) => ({ ...column, target: headerMap[index] })),
    fileName: file.name,
    sheetName,
    entries,
    readyCount,
    issueCount: entries.length - readyCount,
    createCount,
    updateCount,
  };
}

function mapImportRow(
  row: SpreadsheetRow,
  headerMap: string[],
  byId: Map<number, EventRecord>,
  byInternalName: Map<string, EventRecord>,
  rowNumber: number
): ImportPreviewEntry {
  const rawValues: Record<string, string> = {};

  headerMap.forEach((key, index) => {
    if (!key) {
      return;
    }
    rawValues[key] = normalizeImportedCell(row[index]);
  });

  const explicitInternalName = normalizeInternalName(rawValues.internal_name || "");
  const parsedId = Number.parseInt(rawValues.id || "", 10);
  const matchedById = Number.isFinite(parsedId) ? byId.get(parsedId) || null : null;
  const matchedByInternalName = explicitInternalName ? byInternalName.get(explicitInternalName) || null : null;
  const matchedEvent = matchedById || matchedByInternalName;
  const baseForm = matchedEvent
    ? toEventForm(matchedEvent)
    : {
        ...createEmptyEventForm(),
        publication_status: DEFAULT_IMPORTED_EVENT_PUBLICATION_STATUS,
      };

  const normalizedDate = normalizeImportedDate(rawValues.date || baseForm.date);
  const normalizedStartTime = normalizeImportedTime(rawValues.start_time || baseForm.start_time);
  const normalizedEndTime = normalizeImportedTime(rawValues.end_time || baseForm.end_time);
  const displayName = rawValues.display_name || baseForm.display_name;
  const generatedInternalName = buildGeneratedImportInternalName({
    display_name: displayName,
    date: normalizedDate,
    start_time: normalizedStartTime,
  });

  const form: EventFormState = {
    ...baseForm,
    id: matchedEvent?.id || null,
    internal_name: explicitInternalName || (matchedEvent ? baseForm.internal_name : generatedInternalName),
    display_name: displayName,
    date: normalizedDate,
    start_time: normalizedStartTime,
    end_time: normalizedEndTime,
    publication_status: normalizeEventPublicationStatus(
      rawValues.publication_status || baseForm.publication_status,
      matchedEvent ? "published" : DEFAULT_IMPORTED_EVENT_PUBLICATION_STATUS
    ),
    timezone: rawValues.timezone || baseForm.timezone,
    location_name: rawValues.location_name || baseForm.location_name,
    ...(() => {
      const parsedAddress = parseEventAddress(rawValues.location_address || "");
      return {
        location_street_number: rawValues.location_street_number || parsedAddress.location_street_number || baseForm.location_street_number,
        location_street: rawValues.location_street || parsedAddress.location_street || baseForm.location_street,
        location_postal_code: rawValues.location_postal_code || parsedAddress.location_postal_code || baseForm.location_postal_code,
        location_city: rawValues.location_city || parsedAddress.location_city || baseForm.location_city,
        location_address: composeEventAddress({
          location_street_number:
            rawValues.location_street_number || parsedAddress.location_street_number || baseForm.location_street_number,
          location_street: rawValues.location_street || parsedAddress.location_street || baseForm.location_street,
          location_postal_code:
            rawValues.location_postal_code || parsedAddress.location_postal_code || baseForm.location_postal_code,
          location_city: rawValues.location_city || parsedAddress.location_city || baseForm.location_city,
          location_address: rawValues.location_address || baseForm.location_address,
        }),
      };
    })(),
    description: rawValues.description || baseForm.description,
    practical_info: rawValues.practical_info || baseForm.practical_info,
    facebook_url: rawValues.facebook_url || baseForm.facebook_url,
    price_label: rawValues.price_label || baseForm.price_label,
    price_amount: normalizeImportedAmount(rawValues.price_amount || baseForm.price_amount),
    price_currency: (rawValues.price_currency || baseForm.price_currency || "EUR").trim().toUpperCase(),
    illustration_kind: normalizeIllustrationKind(rawValues.illustration_kind || baseForm.illustration_kind),
    illustration_reference: rawValues.illustration_reference || baseForm.illustration_reference,
    illustration_alt_text: rawValues.illustration_alt_text || baseForm.illustration_alt_text,
  };

  const issues: string[] = [];
  if (!form.display_name.trim()) {
    issues.push("Nom d'affichage manquant.");
  }
  if (!normalizeInternalName(form.internal_name || form.display_name)) {
    issues.push("Nom interne manquant.");
  }
  if (form.publication_status === "published" && !form.date) {
    issues.push("Date manquante.");
  }
  if (form.publication_status === "published" && !form.start_time) {
    issues.push("Heure de début manquante.");
  }
  if (rawValues.publication_status && !normalizeEventPublicationStatus(rawValues.publication_status, "")) {
    issues.push("Statut de diffusion invalide. Utilisez draft ou published.");
  }

  return {
    rowNumber,
    mode: matchedEvent ? "update" : "create",
    matchedEventId: matchedEvent?.id || null,
    form,
    issues,
  };
}

function resolveImportColumnKey(value: string | number | boolean | Date | null | undefined) {
  const cleanedValue = normalizeImportColumn(value);
  if (!cleanedValue) {
    return "";
  }

  const match = Object.entries(IMPORT_COLUMN_ALIASES).find(([, aliases]) => aliases.includes(cleanedValue));
  return match ? match[0] : cleanedValue;
}


function normalizeImportedCell(value: string | number | boolean | Date | null | undefined) {
  return spreadsheetCellToString(value);
}

function normalizeImportedDate(value: string) {
  const cleanedValue = String(value || "")
    .trim()
    .replace(/\s+.*/, "")
    .replace(/\./g, "/")
    .replace(/-/g, "/");

  if (!cleanedValue) {
    return "";
  }
  if (/^\d{4}\/\d{2}\/\d{2}$/.test(cleanedValue)) {
    return cleanedValue.replace(/\//g, "-");
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value || "").trim())) {
    return String(value || "").trim();
  }
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(cleanedValue)) {
    const [first, second, year] = cleanedValue.split("/");
    const firstNumber = Number.parseInt(first, 10);
    const secondNumber = Number.parseInt(second, 10);
    const day = secondNumber > 12 && firstNumber <= 12 ? second : first;
    const month = secondNumber > 12 && firstNumber <= 12 ? first : second;
    return `${year}-${padNumber(month)}-${padNumber(day)}`;
  }
  if (/^\d{1,2}\/\d{1,2}\/\d{2}$/.test(cleanedValue)) {
    const [first, second, year] = cleanedValue.split("/");
    const firstNumber = Number.parseInt(first, 10);
    const secondNumber = Number.parseInt(second, 10);
    const day = secondNumber > 12 && firstNumber <= 12 ? second : first;
    const month = secondNumber > 12 && firstNumber <= 12 ? first : second;
    return `20${padNumber(year)}-${padNumber(month)}-${padNumber(day)}`;
  }
  return String(value || "").trim();
}

export function normalizeImportedTime(value: string) {
  const cleanedValue = String(value || "")
    .trim()
    .replace(/[hH]/g, ":")
    .replace(/\s+/g, "");

  if (!cleanedValue) {
    return "";
  }

  const explicitTime = cleanedValue.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (explicitTime) {
    return `${padNumber(explicitTime[1])}:${explicitTime[2]}`;
  }

  const hourOnlyTime = cleanedValue.match(/^(\d{1,2}):?$/);
  if (hourOnlyTime) {
    return `${padNumber(hourOnlyTime[1])}:00`;
  }

  const compactTime = cleanedValue.match(/^(\d{1,2})(\d{2})$/);
  if (compactTime) {
    return `${padNumber(compactTime[1])}:${compactTime[2]}`;
  }

  return String(value || "").trim();
}

function normalizeEventPublicationStatus(value: string, fallback: ""): "" | "draft" | "published";
function normalizeEventPublicationStatus(value: string, fallback?: "draft" | "published"): "draft" | "published";
function normalizeEventPublicationStatus(
  value: string,
  fallback: "draft" | "published" | "" = "published"
): "" | "draft" | "published" {
  const cleanedValue = String(value || "").trim().toLowerCase();
  if (!cleanedValue) {
    return fallback;
  }

  const aliases: Record<string, "draft" | "published"> = {
    brouillon: "draft",
    draft: "draft",
    publie: "published",
    published: "published",
  };

  return aliases[cleanedValue] || fallback;
}

function normalizeImportedAmount(value: string) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, "")
    .replace(",", ".");
}

function normalizeIllustrationKind(value: string) {
  if (value === "external_url" || value === "asset_ref") {
    return value;
  }
  return "none";
}

function formatAmountCents(value: number) {
  return (value / 100).toFixed(2);
}

function padNumber(value: string) {
  return String(value || "").padStart(2, "0");
}

function escapeCsvCell(value: string) {
  const stringValue = String(value ?? "");
  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
}

function triggerFileDownload(blob: Blob, fileName: string) {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = fileName;
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}
